import { createSelector } from "reselect";
import AppState from "../AppState";
import { HeatMapDateRangeState, HeatMapState } from "./HeatMapTypes";
import {
  computeHeatMapGradient,
  resolveDateRange,
  ResolvedDateRange,
} from "../../utils/HeatMapUtils";
import {
  selectCurrentPage,
  selectContentSlots,
  selectProductListSequenceMode,
  selectProductListsFetched,
  selectProductListEditSequence,
} from "../product-list/ProductListSelectors";
import {
  selectIsFetchedBySearch,
  selectProductListBySearch,
} from "../product/ProductSelectors";
import { getCurrentPageProductIds } from "../../utils/ProductUtil";

/**
 * Number of catalog pages per heat map batch. With the default page size
 * of 24, a batch holds up to 240 products. The exact page size is
 * resolved per-page by `getCurrentPageProductIds`.
 */
const PAGES_PER_BATCH = 10;

export const selectHeatMapState = (state: AppState): HeatMapState =>
  state.heatmap;

export const selectHeatMapMetric = (state: AppState): string =>
  state.heatmap.metric;

export const selectHeatMapDateRange = (
  state: AppState,
): HeatMapDateRangeState => state.heatmap.dateRange;

export const selectHeatMapHighColor = (state: AppState): string =>
  state.heatmap.highColor;

export const selectHeatMapLowColor = (state: AppState): string =>
  state.heatmap.lowColor;

export const selectHeatMapIsFetching = (state: AppState): boolean =>
  state.heatmap.pendingRequests > 0;

export const selectHeatMapLastFetchedStartDate = (
  state: AppState,
): string | undefined => state.heatmap.lastFetchedStartDate;

export const selectHeatMapLastFetchedEndDate = (
  state: AppState,
): string | undefined => state.heatmap.lastFetchedEndDate;

export const selectHeatMapLastSettledRangeKey = (state: AppState): string =>
  state.heatmap.lastSettledRangeKey;

export const selectHeatMapFreshProductCodes = (
  state: AppState,
): Record<string, true> => state.heatmap.freshProductCodes;

/**
 * Memoized: 8-color gradient computed from the high/low color choice.
 * Recomputes only when high or low color changes.
 */
export const selectHeatMapGradient = createSelector(
  [selectHeatMapHighColor, selectHeatMapLowColor],
  (high, low) => computeHeatMapGradient(high, low),
);

export const selectHeatMapResolvedDateRange = createSelector(
  [selectHeatMapDateRange],
  (dateRange): ResolvedDateRange =>
    resolveDateRange(
      dateRange.option,
      dateRange.customStartDate,
      dateRange.customEndDate,
    ),
);

/**
 * Memoized: product IDs displayed on the **current** product list page,
 * matching the same pagination + content-slot logic as the main grid.
 *
 * The heat map planogram is scoped to the current page only — not the entire
 * category — so the visualization aligns with what the merchandiser is
 * currently viewing.
 */
export interface HeatMapBatchPage {
  pageNumber: number;
  productIds: string[];
}

export interface HeatMapBatchPages {
  pages: HeatMapBatchPage[];
  totalProductCount: number;
}

/**
 * Memoized: the up-to-10 catalog pages that make up the current heat map
 * batch (≤240 products). The active batch is determined by the catalog
 * page the user is on at modal open: pages 1–10 → batch 0, 11–20 → batch
 * 1, etc.
 *
 * Each entry's `pageNumber` is the actual catalog page number (1-indexed)
 * and is used for the `P#N` label in the planogram.
 *
 * Pages whose product list comes back empty (end-of-catalog) are dropped,
 * so a batch may have fewer than 10 entries near the end.
 */
export const selectHeatMapBatchPages = createSelector(
  [
    selectContentSlots,
    selectCurrentPage,
    selectIsFetchedBySearch,
    selectProductListBySearch,
    selectProductListSequenceMode,
    selectProductListsFetched,
    selectProductListEditSequence,
  ],
  (
    contentSlots,
    currentPage,
    isFetchedBySearch,
    productsBySearch,
    sequenceMode,
    products,
    editProducts,
  ): HeatMapBatchPages => {
    const batchIndex = Math.max(
      0,
      Math.floor((currentPage - 1) / PAGES_PER_BATCH),
    );
    const pages: HeatMapBatchPage[] = [];
    let totalProductCount = 0;

    for (let i = 0; i < PAGES_PER_BATCH; i++) {
      const pageNumber = batchIndex * PAGES_PER_BATCH + i + 1;
      const productIds = getCurrentPageProductIds(
        contentSlots,
        pageNumber,
        isFetchedBySearch,
        productsBySearch,
        sequenceMode,
        products,
        editProducts,
      ) as string[];
      if (productIds && productIds.length > 0) {
        pages.push({ pageNumber, productIds });
        totalProductCount += productIds.length;
      }
    }

    return { pages, totalProductCount };
  },
);

/** @deprecated kept for backward compatibility — use `selectHeatMapBatchPages`. */
export const selectHeatMapCurrentPageProductIds = createSelector(
  [
    selectContentSlots,
    selectCurrentPage,
    selectIsFetchedBySearch,
    selectProductListBySearch,
    selectProductListSequenceMode,
    selectProductListsFetched,
    selectProductListEditSequence,
  ],
  (
    contentSlots,
    currentPage,
    isFetchedBySearch,
    productsBySearch,
    sequenceMode,
    products,
    editProducts,
  ): string[] => {
    return getCurrentPageProductIds(
      contentSlots,
      currentPage,
      isFetchedBySearch,
      productsBySearch,
      sequenceMode,
      products,
      editProducts,
    ) as string[];
  },
);
